import { ref, uploadBytesResumable, getDownloadURL, deleteObject } from 'firebase/storage';
import { storage } from '../config/firebase';

/**
 * Compress an image file using canvas
 * Resizes to max dimensions and compresses quality
 */
const compressImage = (file: File, maxWidth = 1920, maxHeight = 1920, quality = 0.8): Promise<File> => {
  return new Promise((resolve, reject) => {
    // Skip non-compressible formats
    if (file.type === 'image/gif') {
      resolve(file);
      return;
    }

    const img = new Image();
    const url = URL.createObjectURL(file);

    img.onload = () => {
      URL.revokeObjectURL(url);

      let { width, height } = img;

      // Only resize if larger than max dimensions
      if (width <= maxWidth && height <= maxHeight && file.size <= 1024 * 1024) {
        resolve(file);
        return;
      }

      // Calculate new dimensions maintaining aspect ratio
      if (width > maxWidth) {
        height = Math.round((height * maxWidth) / width);
        width = maxWidth;
      }
      if (height > maxHeight) {
        width = Math.round((width * maxHeight) / height);
        height = maxHeight;
      }

      const canvas = document.createElement('canvas');
      canvas.width = width;
      canvas.height = height;
      const ctx = canvas.getContext('2d')!;
      ctx.drawImage(img, 0, 0, width, height);

      canvas.toBlob(
        (blob) => {
          if (!blob) {
            resolve(file);
            return;
          }
          const compressed = new File([blob], file.name, {
            type: 'image/jpeg',
            lastModified: Date.now(),
          });
          resolve(compressed);
        },
        'image/jpeg',
        quality,
      );
    };

    img.onerror = () => {
      URL.revokeObjectURL(url);
      reject(new Error('Failed to load image for compression'));
    };

    img.src = url;
  });
};

/**
 * Upload an image to Firebase Storage with progress tracking
 */
export const uploadImage = async (
  file: File,
  path: string,
  onProgress?: (progress: number) => void,
): Promise<string> => {
  // Compress image before upload
  const compressed = await compressImage(file);

  const timestamp = Date.now();
  const filename = `${timestamp}-${file.name.replace(/[^a-zA-Z0-9.-]/g, '_')}`;
  const storageRef = ref(storage, `${path}/${filename}`);

  return new Promise((resolve, reject) => {
    const uploadTask = uploadBytesResumable(storageRef, compressed, {
      contentType: compressed.type,
    });

    uploadTask.on(
      'state_changed',
      (snapshot) => {
        const progress = Math.round((snapshot.bytesTransferred / snapshot.totalBytes) * 100);
        onProgress?.(progress);
      },
      (error) => reject(error),
      async () => {
        const downloadURL = await getDownloadURL(uploadTask.snapshot.ref);
        resolve(downloadURL);
      },
    );
  });
};

/**
 * Upload multiple images to Firebase Storage. All-or-nothing: if any upload
 * fails, the ones that already succeeded are deleted so no files are orphaned.
 * @param files - Array of image files to upload
 * @param path - Storage path
 * @returns Array of download URLs (in the same order as `files`)
 * @throws if any upload fails (after rolling back the successful ones)
 */
export const uploadImages = async (files: File[], path: string): Promise<string[]> => {
  const results = await Promise.allSettled(files.map((file) => uploadImage(file, path)));
  const uploaded = results
    .filter((r): r is PromiseFulfilledResult<string> => r.status === 'fulfilled')
    .map((r) => r.value);

  const failure = results.find((r) => r.status === 'rejected') as PromiseRejectedResult | undefined;
  if (failure) {
    await Promise.allSettled(uploaded.map((url) => deleteImage(url)));
    throw failure.reason instanceof Error ? failure.reason : new Error('Bild-Upload fehlgeschlagen');
  }
  return uploaded;
};

/**
 * Delete an image from Firebase Storage
 * @param imageUrl - The download URL of the image to delete
 */
export const deleteImage = async (imageUrl: string): Promise<void> => {
  try {
    // Extract path from URL
    const urlObj = new URL(imageUrl);
    const pathStart = urlObj.pathname.indexOf('/o/') + 3;
    const pathEnd = urlObj.pathname.indexOf('?');
    const path = decodeURIComponent(
      urlObj.pathname.substring(pathStart, pathEnd !== -1 ? pathEnd : undefined)
    );

    const storageRef = ref(storage, path);
    await deleteObject(storageRef);
  } catch (error) {
    console.error('Error deleting image:', error);
    // Don't throw error - image might already be deleted or URL might be external
  }
};

/**
 * Validate image file
 * @param file - File to validate
 * @param maxSizeMB - Maximum file size in MB (default: 5MB)
 * @returns Validation result
 */
export const validateImageFile = (
  file: File,
  maxSizeMB: number = 20
): { valid: boolean; error?: string } => {
  // Check file type
  const validTypes = ['image/jpeg', 'image/jpg', 'image/png', 'image/gif', 'image/webp'];
  if (!validTypes.includes(file.type)) {
    return {
      valid: false,
      error: 'Please upload a valid image file (JPEG, PNG, GIF, or WebP)',
    };
  }

  // Check file size
  const maxSizeBytes = maxSizeMB * 1024 * 1024;
  if (file.size > maxSizeBytes) {
    return {
      valid: false,
      error: `Image size must be less than ${maxSizeMB}MB`,
    };
  }

  return { valid: true };
};

/**
 * Create a preview URL for an image file
 * @param file - Image file
 * @returns Object URL for preview
 */
export const createImagePreview = (file: File): string => {
  return URL.createObjectURL(file);
};

/**
 * Revoke an object URL to free up memory
 * @param url - Object URL to revoke
 */
export const revokeImagePreview = (url: string): void => {
  URL.revokeObjectURL(url);
};
